"use client";

import { trackSlateDesignSystemUiModalShowView } from "@faire/web-api--source/events/slateDesignSystem/view/uiModalShow";
import { ScrollStore } from "@faire/web/common/ScrollStore";
import { trackEvent } from "@faire/web/common/trackEvent";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { useCallback, useEffect, useRef } from "react";
import styled, {
  useTheme,
  DefaultTheme as StyledComponentsTheme,
} from "styled-components";

import { SVGIcon } from "../../icons/SVGIcon";
import { ITheme } from "../../Theme";

import { BaseModal } from "./BaseModal";
import { ModalNavigationAbsolute } from "./ModalNavigation/ModalNavigationAbsolute";
import { ModalNavigationFixed } from "./ModalNavigation/ModalNavigationFixed";

type JustifyValues =
  | "center"
  | "flex-start"
  | "flex-end"
  | "space-between"
  | "space-evenly"
  | "space-around";

type AlignValues = "center" | "flex-start" | "flex-end" | "stretch";

/**
 * Default wrapping div for the children of the `Modal`.
 * Exported so that modifications can be made to this for
 * cases where smaller content can be centered by wrapping
 * it in a parent component.
 *
 * @trackcomponent
 */
export const Wrapper = styled.div<{
  $padding: string;
  $justify: JustifyValues;
  $align: AlignValues;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => $align};
  justify-content: ${({ $justify }) => $justify};
  overflow: inherit;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ $padding }) => $padding};
`;

export interface ModalProps {
  children?: React.ReactNode;
  /**
   * the theme passed in from ThemeProvider. You likely don't need to ever explicitly set this
   * @default "DefaultTheme from @faire/web/ui/theme/DefaultTheme"
   */
  theme?: StyledComponentsTheme;
  /**
   * Use primary coloring
   * @default false
   */
  primary?: boolean;
  /**
   * Sets the padding of the modal container itself. Should be set to 0 if used in conjunction with stickyNav
   * @default "'64px 12px' on mobile; '30px 57px' on desktop"
   */
  padding?: string;
  /**
   * The trigger for whether the modal is open or not on the page. When set to true, the modal and overlay will appear
   * @default false
   */
  isOpen?: boolean;
  /**
   * Whether to allow the background to scroll while this modal
   * is open. By default, `.noscroll` will be added to the body,
   * and the styles in `GlobalModalStyle` will prevent scrolling
   * @default false
   */
  allowBackgroundScroll?: boolean;
  /**
   * The width of the modal content. Has no affect on the overlay
   * @default 500
   */
  width?: string;
  /**
   * The height of the modal content. Has no affect on the overlay
   * @default 600
   */
  height?: string;
  /**
   * If onBack is defined we will show the back button in the top left
   */
  onBack?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Allows you to style the back icon on the modal for navigation.
   * @default {}
   */
  backIconProps?: Partial<SVGIcon.IProps>;
  /**
   * If onForward is defined we will show the forward button in the top left
   */
  onForward?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Allows you to style the back icon on the modal for navigation
   * @default {}
   */
  forwardIconProps?: Partial<SVGIcon.IProps>;
  /**
   * event handler triggered when you click the close icon in the modal
   */
  onClose?: (event: any) => void;
  /**
   * Sets whether or not to show the close button in the modal
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Allows you to style the close icon on the modal for navigation
   * @default {}
   */
  closeIconProps?: Partial<SVGIcon.IProps>;
  /**
   * Replaces the absolute positioned nav with a sticky nav with content beneath it that can scroll behind it
   * @default false
   */
  stickyNav?: boolean;
  /**
   * provided so styled(Modal) works properly.
   */
  className?: string;
  /**
   * Allows you style the base modal container. See @faire/web/ui/Modal/BaseModal.tsx for base styles
   */
  contentStyle?: ReactModal.Styles["content"];
  /**
   * Allows you to style the base overlay. See @faire/web/ui/Modal/BaseModal.tsx for base overlay styles
   */
  overlayStyle?: ReactModal.Styles["overlay"];
  /**
   * Allows you to render the Modal as a child of a DOM element. Expects a ref value.
   * @default undefined
   */
  parentElement?: HTMLElement;
  /**
   * returns focus to the element that had focus prior to opening the modal after the modal is closed
   * @default true
   */
  shouldReturnFocusAfterClose?: ReactModal.Props["shouldReturnFocusAfterClose"];
  /**
   * Allows the modal to close if the overlay is clicked
   * @default true
   */
  shouldCloseOnOverlayClick?: boolean;
  /**
   * The modal content wrapper styles default to display: flex and flex-direction: column. This prop allows you to set the justify-content (veritcal align) property of the modal.
   * @default center
   */
  justify?: JustifyValues;
  /**
   * The modal content wrapper styles default to display: flex and flex-direction: column. This prop allows you to set the align-items (horizontal align) property of the modal.
   * @default center
   */
  align?: AlignValues;
  /**
   * Testing option to avoid requiring the app element to be defined.
   * Passed through to the react modal.
   */
  ariaHideApp?: boolean;
  /**
   * property used to find the modal e2e events
   */
  "data-test-id"?: string;
  /**
   * If set, the given path will be posted as a tracking event upon mounting.
   * Note that this is just an ID, not the full event path.
   * @example 'signUp'
   */
  postEventModalId?: string;
  /**
   * Ref that points to the base modal (not the wrapper component).
   */
  setContentRef?: (instance: HTMLDivElement) => void;
  /**
   * Render without using a portal. Enable this for portals that must be rendered in SSR.
   *
   * Portals make it possible to style the modal component independently of the
   * parent component and ensure that the modal component is on top of other
   * elements in the UI, regardless of the parent component's styling or
   * layout. Therefore, leave this disabled if you do not need SSR.
   *
   * IMPORTANT: If you set this to true, the element with ID "main" will be
   * hidden to screen readers instead of the "root" element.
   */
  noPortal?: boolean;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

/**
 * @deprecated use Modal from slate/Modal
 * @deprecatedBy "@faire/web/slate/Modal/Modal#Modal"
 * @trackcomponent
 */
export const Modal: React.FC<ModalProps> = ({
  primary,
  padding,
  children,
  onClose,
  closeIconProps = {},
  showCloseButton = true,
  onBack,
  backIconProps = {},
  onForward,
  forwardIconProps = {},
  stickyNav = false,
  width,
  height,
  className,
  parentElement,
  contentStyle,
  overlayStyle,
  shouldReturnFocusAfterClose,
  shouldCloseOnOverlayClick,
  justify = "flex-start",
  align = "center",
  ariaHideApp,
  ["data-test-id"]: dataTestId,
  setContentRef,
  postEventModalId,
  isOpen = true,
  allowBackgroundScroll,
  noPortal,
  ["aria-labelledby"]: ariaLabelledBy,
  ["aria-describedby"]: ariaDescribedBy,
}) => {
  const postedEvent = useRef(false);
  const shouldPopScrollStore = useRef(false);

  const postShowModalEvent = useCallback(() => {
    postedEvent.current = true;
    if (postEventModalId) {
      trackEvent(`/ui/modal/show/${postEventModalId}`);
      trackSlateDesignSystemUiModalShowView(postEventModalId);
    }
  }, [postEventModalId]);

  useEffect(() => {
    if (isOpen && !postedEvent.current) {
      postShowModalEvent();
    }
  }, [isOpen, postShowModalEvent]);

  const handleAfterOpen = () => {
    if (!allowBackgroundScroll && !shouldPopScrollStore.current) {
      ScrollStore.get().pushNoScroll();
      shouldPopScrollStore.current = true;
    }
  };

  const handleAfterClose = () => {
    maybePopScrollStore();
  };

  const maybePopScrollStore = () => {
    if (shouldPopScrollStore.current) {
      ScrollStore.get().popNoScroll();
      shouldPopScrollStore.current = false;
    }
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    // React synthetic events bubble through portals.
    // stopPropagation() will stop click events from bubbling up to the parent component.
    e.stopPropagation();
  };

  const viewport = useViewport();
  const theme = useTheme();
  const nestedTheme: ITheme = {
    ...theme,
    buttons: theme.buttons,
  };
  const navProps = {
    onClose: onClose,
    onBack: onBack,
    onForward: onForward,
    primary: primary,
    showCloseButton: showCloseButton,
    closeIconProps: closeIconProps,
    backIconProps: backIconProps,
    forwardIconProps: forwardIconProps,
  };

  return (
    <ThemeProvider theme={nestedTheme}>
      <BaseModal
        contentRef={setContentRef}
        ariaHideApp={ariaHideApp}
        primary={primary}
        className={className}
        isOpen={isOpen}
        shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
        onAfterClose={handleAfterClose}
        onAfterOpen={handleAfterOpen}
        parentSelector={
          parentElement === undefined ? undefined : () => parentElement
        }
        style={{
          content: {
            maxHeight: "100vh",
            height: `${viewport.isMobile ? "100%" : (height ?? "600px")}`,
            width: `${viewport.isMobile ? "100%" : (width ?? "500px")}`,
            ...(contentStyle ?? {}),
          },
          overlay: {
            ...(noPortal
              ? {
                  position: "fixed",
                  width: "100vw",
                  height: "100vh",
                  top: 0,
                  left: 0,
                }
              : {}),
            ...(overlayStyle ?? {}),
          },
        }}
        onRequestClose={onClose}
        data-test-id="faireModal"
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        noPortal={noPortal}
        aria={{
          labelledby: ariaLabelledBy,
          describedby: ariaDescribedBy,
        }}
      >
        <Wrapper
          onClick={handleWrapperClick}
          data-test-id={dataTestId}
          $padding={padding ?? (viewport.isMobile ? "64px 12px" : "30px 57px")}
          $justify={justify}
          $align={align}
        >
          {stickyNav ? (
            <ModalNavigationFixed {...navProps} />
          ) : (
            <ModalNavigationAbsolute {...navProps} />
          )}
          {children}
        </Wrapper>
      </BaseModal>
    </ThemeProvider>
  );
};
